// src/components/FarmMark/LeaderboardEntry.js

/**
 * Single row in a FarmMark leaderboard. Flat, flush row — no enclosing card,
 * no divider lines. Rank, avatar, clickable display name, @username,
 * IP aligned to the right.
 */

import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function LeaderboardEntry({ rank, user }) {
  const { t } = useTranslation("farmMark");

  const rankStyle =
    rank === 1
      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 font-bold"
      : rank === 2
        ? "bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300 font-semibold"
        : rank === 3
          ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 font-semibold"
          : "bg-gray-50 text-gray-600 dark:bg-neutral-900 dark:text-gray-400";

  const profilePath = user?.userId ? `/profile/${user.userId}` : null;
  const displayName = user?.displayName || t("leaderboard.user");
  const username = user?.username || "";

  const nameBlock = (
    <div className="min-w-0">
      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
        {displayName}
      </div>
      {username ? (
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
          @{username}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-neutral-900 transition-colors">
      {/* Rank */}
      <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full text-xs ${rankStyle}`}>
        {rank}
      </div>

      {/* Avatar */}
      <div className="w-8 h-8 flex-shrink-0 rounded-full overflow-hidden bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
        {user.photoURL ? (
          <img src={user.photoURL} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <span className="text-xs font-semibold text-green-700 dark:text-green-300">
            {(displayName || "?").charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      {/* Name + username (clickable) */}
      <div className="flex-1 min-w-0">
        {profilePath ? (
          <Link
            to={profilePath}
            className="block hover:text-green-700 dark:hover:text-green-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 rounded"
          >
            {nameBlock}
          </Link>
        ) : (
          nameBlock
        )}
      </div>

      {/* IP */}
      <div className="flex-shrink-0 text-sm font-bold text-green-700 dark:text-green-400">
        {(user.totalIP || 0).toLocaleString()} {t("leaderboard.ip")}
      </div>
    </div>
  );
}